import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Compass, Loader2, KeyRound, Tent } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

export default function Beta() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return toast.error("Enter your access code");
    setSubmitting(true);
    try {
      await api.post("/beta/redeem", { code: code.trim().toUpperCase() });
      toast.success("Welcome to CampCompass UK · Beta");
      // Force a full reload so the SPA gate rechecks server-side cookie state
      window.location.replace(params.get("next") || "/");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ff4500] via-[#e03d00] to-[#a82e00] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden" data-testid="beta-page">
      <div className="absolute inset-0 opacity-15" style={{
        backgroundImage: "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />

      <div className="relative max-w-md w-full">
        <div className="flex items-center gap-2 mb-4 justify-center">
          <Compass className="w-7 h-7" strokeWidth={1.75} />
          <span className="font-display text-2xl tracking-tight">CampCompass UK</span>
        </div>
        <span className="block text-center eyebrow !text-white/80 mb-6">Beta · Invitation only</span>

        <h1 className="font-display text-3xl sm:text-4xl text-center leading-tight tracking-tight">
          Got an access code?
        </h1>
        <p className="text-sm text-white/85 leading-snug mt-3 text-center">
          We're testing CampCompass UK with a small group of campers before launch.
          Enter the code your invite included to come on in.
        </p>

        <form onSubmit={submit} className="mt-8 bg-white/10 backdrop-blur-md border-2 border-white/30 p-5 sm:p-6">
          <label className="eyebrow !text-white/70 block mb-2 flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Access code
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
            placeholder="XXXXXXXX"
            autoComplete="off"
            autoCapitalize="characters"
            className="w-full bg-white/95 text-[#222] border-2 border-white px-4 py-3 mono text-2xl tracking-[0.3em] text-center font-bold focus:outline-none focus:ring-2 focus:ring-white"
            data-testid="beta-code-input"
            disabled={submitting}
            autoFocus
          />
          <button
            type="submit"
            disabled={submitting || !code.trim()}
            className="mt-4 w-full bg-white text-[#ff4500] font-bold uppercase tracking-wider text-sm px-5 py-3.5 inline-flex items-center justify-center gap-2 shadow-[0_4px_0_0_#111] hover:shadow-[0_2px_0_0_#111] hover:translate-y-[2px] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
            data-testid="beta-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tent className="w-4 h-4" strokeWidth={2.5} />}
            {submitting ? "Checking…" : "Enter the camp"}
          </button>
        </form>

        <p className="text-xs text-center text-white/70 mt-6 leading-snug">
          Don't have a code? <button onClick={() => nav("/login")} className="underline font-bold">Existing user? Log in</button>
        </p>
      </div>
    </div>
  );
}
